<template>
  <div>
    <v-fade-transition>
      <div class="d-flex flex-wrap align-center mb-2" v-if="orgLevel">
        <Subtitle customClass="mb-0">
          {{ orgLevel && orgLevel.value && orgLevel.value.name }}
        </Subtitle>
        <v-fade-transition>
          <Hyperlink
            small
            text="Ändern"
            customClass="mx-1 mb-0"
            v-if="showChangeLevelButton"
            :clickHandler="toggleShowLevelField"
          />
        </v-fade-transition>
        <v-icon color="textColor" :size="16">
          {{ chevron }}
        </v-icon>
        <Subtitle customClass="mb-0">
          {{ (group && group.name) || "..." }}
        </Subtitle>
        <v-fade-transition>
          <Hyperlink
            small
            text="Ändern"
            customClass="mx-1 mb-0"
            v-if="showChangeGroupButton"
            :clickHandler="toggleShowGroupField"
          />
        </v-fade-transition>
      </div>
    </v-fade-transition>
    <v-expand-transition>
      <v-select
        v-if="showLevelField"
        attach=".client__select"
        :items="orgLevelOptions"
        @input="emitLevel"
        :value="orgLevel"
        return-object
        item-text="value.name"
        item-value="value.id"
        required
        filled
        background-color="lightGrey"
        persistent-placeholder
        class="suus__group-select client__select"
      >
        <template v-slot:label>
          <FieldLabel :label="orgLevelLabel" mandatory />
        </template>
      </v-select>
    </v-expand-transition>

    <v-expand-transition>
      <GroupMemberSingleSelect
        v-if="showGroupField"
        :label="groupLabel"
        isMandatory
        :getOptions="getGroups"
        :getOptionsParams="getGroupsParams"
        @input="emitGroupChange"
        :value="group"
        @search="handleGroupSearch"
        item-text="name"
        attach=".group__select"
        required
        id="topic-form__groupInput"
        class="group__select"
        :rules="inputGroupValidation"
        :hide-no-data="false"
      />
    </v-expand-transition>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, inject, PropType, ref, watch } from "vue"
import Subtitle from "components/UI/Subtitle.vue"
import Hyperlink from "components/UI/Hyperlink.vue"
import MemberService, { OrgLevel } from "features/member/member.service"
import { mdiChevronRight } from "@mdi/js"
import { Group } from "suus-api"
import FieldLabel from "components/UI/FieldLabel.vue"
import GroupMemberSingleSelect from "components/GroupMemberSingleSelect.vue"
import { groupApi } from "store/api"

export default defineComponent({
  name: "GroupSelect",
  components: { GroupMemberSingleSelect, FieldLabel, Hyperlink, Subtitle },
  props: {
    orgLevel: {
      type: Object as PropType<OrgLevel>,
      required: false,
      default: null,
    },
    orgLevelOptions: {
      type: Array as PropType<OrgLevel[]>,
      required: false,
      default: () => [],
    },
    orgLevelLabel: {
      type: String,
      required: false,
      default: "",
    },
    group: {
      type: Object as PropType<Group>,
      required: false,
      default: null,
    },
    groupLabel: {
      type: String,
      required: false,
      default: "",
    },
    isEdit: {
      type: Boolean,
      required: false,
      default: false,
    },
    isGroupPage: {
      type: Boolean,
      required: false,
      default: false,
    },
    inputGroupValidation: { type: Array as PropType<any[]>, default: () => [] },
    queryParams: {
      type: Object as PropType<{ forum?: boolean; calendar?: boolean }>,
      required: false,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const showLevelField = ref(false)
    const showGroupField = ref(!props.isGroupPage || !props.group)

    const memberService: MemberService = inject("MemberService", undefined)

    watch(
      () => props.isGroupPage,
      () => {
        showGroupField.value = !props.isGroupPage
      }
    )

    const showChangeLevelButton = computed(
      () =>
        props.orgLevelOptions.length > 1 &&
        !showLevelField.value &&
        !props.isEdit
    )

    function toggleShowLevelField() {
      showLevelField.value = !showLevelField.value
    }

    function emitLevel(orgLevel: OrgLevel) {
      emit("orgLevelChange", orgLevel)
    }

    const showChangeGroupButton = computed(() => !showGroupField.value)

    function toggleShowGroupField() {
      emit("toggleShowGroupField")
      showGroupField.value = !showGroupField.value
    }

    let selectedClientId = ref(null)
    let selectedLevelId = ref(null)

    function getClientIdFromInputLevelOrClient(inputLevelOrClient) {
      return inputLevelOrClient?.type === "Client"
        ? inputLevelOrClient.value?.id
        : null
    }

    function getLevelIdFromInputLevelOrClient(inputLevelOrClient) {
      return inputLevelOrClient?.type === "Level"
        ? inputLevelOrClient.value?.id
        : null
    }

    function getClientIdFromFromGroup(group) {
      return group.client_id
    }

    function getLevelIdFromFromGroup(group) {
      return group.level_id
    }

    let groupSearchString = ref(null)

    function handleGroupSearch(val) {
      groupSearchString.value = val
    }

    watch(
      props,
      (newVal, oldVal) => {
        if (newVal.group) {
          selectedClientId.value = getClientIdFromFromGroup(newVal.group)
          selectedLevelId.value = getLevelIdFromFromGroup(newVal.group)
        } else {
          selectedClientId.value = getClientIdFromInputLevelOrClient(
            newVal.orgLevel
          )
          selectedLevelId.value = getLevelIdFromInputLevelOrClient(
            newVal.orgLevel
          )
        }
        emit("updateLevelSelection", {
          selectedLevelId: selectedLevelId.value,
          selectedClientId: selectedClientId.value,
        })
      },
      { immediate: true }
    )

    const getGroupsParams = computed(() => {
      return {
        clientId: selectedClientId.value,
        levelId: selectedLevelId.value,
        groupSearchString: groupSearchString.value,
        ...props.queryParams,
      }
    })

    const emitGroupChange = (group) => {
      emit("groupChange", group)
    }

    // This is a workaround for when the users client has no groups that can be chosen for a new event or new topic
    // See: SN-1801
    // Reason: A migration for Vuetify and Vue is planned, i tried fixing this with the minimal amount of changes
    // TODO: After the migrations, refactor this solution (look at the ParticipationCircleFields.vue also)
    //       The slicing of these components is complex and should be refactored
    const groupSelectInitialized = ref(false)

    async function getGroups({
      levelId,
      clientId,
      groupSearchString,
      forum,
      calendar,
    }) {
      const memberId = memberService?.currentMember?.value?.personal_record?.id

      const data = await groupApi.getGroups(
        groupSearchString ?? undefined,
        levelId ?? undefined,
        clientId ?? undefined,
        undefined,
        memberId ?? undefined,
        undefined,
        undefined,
        undefined,
        calendar,
        forum
      )

      if (!groupSelectInitialized.value) {
        groupSelectInitialized.value = true
        const results = data.data.results
        if (results.length === 0) {
          const firstLevel = props.orgLevelOptions.find(
            (level) => level.type === "Level"
          )
          if (firstLevel) {
            emitLevel(firstLevel)
          }
        }
      }

      return data.data.results
    }

    //when user selects new inputLevelOrClient the Group input is resetted
    watch(
      () => props.orgLevel,
      (newVal: OrgLevel, oldVal: OrgLevel) => {
        if (oldVal?.value?.id && newVal?.value?.id !== oldVal.value.id) {
          emitGroupChange(null)
        }
      }
    )

    return {
      showLevelField,
      showGroupField,
      showChangeLevelButton,
      toggleShowLevelField,
      emitLevel,
      showChangeGroupButton,
      toggleShowGroupField,
      getGroupsParams,
      emitGroupChange,
      handleGroupSearch,
      getGroups,
      chevron: mdiChevronRight,
    }
  },
})
</script>
